
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { encolar, esFalloDeRed } from '../lib/colaOffline';
import type { FeedingRecord, SleepRecord, DiaperRecord, GrowthRecord, Milestone } from '../lib/supabase';

/**
 * Guarda un registro, y si no hay señal lo deja en la cola local.
 *
 * La app se usa de madrugada, muchas veces sin buena cobertura. Antes,
 * si fallaba la subida el dato se perdía y encima el usuario ni se
 * enteraba. Ahora se guarda igual y sube solo cuando vuelve el
 * internet.
 *
 * Solo se encola cuando el fallo ES de red. Un dato inválido o un
 * permiso denegado se devuelven como error de verdad: encolarlos
 * dejaría basura reintentándose para siempre.
 */
async function guardarConRespaldo<T>(
  tabla: string,
  datos: Record<string, unknown>,
): Promise<{ data: T | null; error: unknown; pendiente: boolean }> {
  try {
    const { data, error } = await supabase.from(tabla).insert([datos]).select().single();
    if (error) throw error;
    return { data: data as T, error: null, pendiente: false };
  } catch (error) {
    if (esFalloDeRed(error)) {
      return { data: encolar<T>(tabla, datos), error: null, pendiente: true };
    }
    console.error(`Error guardando en ${tabla}:`, error);
    return { data: null, error, pendiente: false };
  }
}

// Hook consolidado para todos los registros
export const useRecords = (babyId: string | null) => {
  const feedingHook = useFeedingRecords(babyId);
  const sleepHook = useSleepRecords(babyId);
  const diaperHook = useDiaperRecords(babyId);
  const growthHook = useGrowthRecords(babyId);

  return {
    feedingRecords: feedingHook.records,
    sleepRecords: sleepHook.records,
    diaperRecords: diaperHook.records,
    growthRecords: growthHook.records,
    loading: feedingHook.loading || sleepHook.loading || diaperHook.loading || growthHook.loading,
    // Exponer funciones de cada hook
    feeding: feedingHook,
    sleep: sleepHook,
    diaper: diaperHook,
    growth: growthHook,
  };
};

// Hook para registros de alimentación
export const useFeedingRecords = (babyId: string | null) => {
  const [records, setRecords] = useState<FeedingRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (babyId) {
      fetchRecords();
    } else {
      setRecords([]);
      setLoading(false);
    }
  }, [babyId]);

  const fetchRecords = async () => {
    try {
      const { data, error } = await supabase
      // Solo lo reciente.
      //
      // Antes se pedía TODO el historial del bebé cada vez que se abría
      // la pantalla. Con un bebé de un año son miles de filas: se
      // descargaban enteras, con datos móviles, para mostrar las de
      // arriba. Y encima el servidor corta en 1000 sin avisar, así que
      // el "historial completo" ya venía recortado en silencio.
      //
      // 500 cubre semanas de uso intenso. Para llevarse todo está
      // "Descargar mis datos", que sí trae hasta la última fila.
        .from('feeding_records')
        .select('*')
        .eq('baby_id', babyId)
        .order('start_time', { ascending: false })
        .limit(500);

      if (error) throw error;
      setRecords(data || []);
    } catch (error) {
      console.error('Error fetching feeding records:', error);
    } finally {
      setLoading(false);
    }
  };

  const addRecord = async (recordData: Omit<FeedingRecord, 'id' | 'created_at'>) => {
    const { data, error, pendiente } = await guardarConRespaldo<FeedingRecord>(
      'feeding_records',
      recordData as Record<string, unknown>,
    );
    if (data) setRecords(prev => [data, ...prev]);
    return { data, error, pendiente };
  };

  const updateRecord = async (id: string, updates: Partial<FeedingRecord>) => {
    try {
      const { data, error } = await supabase
        .from('feeding_records')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setRecords(prev => prev.map(record => record.id === id ? data : record));
      return { data, error: null };
    } catch (error) {
      console.error('Error updating feeding record:', error);
      return { data: null, error };
    }
  };

  const deleteRecord = async (id: string) => {
    try {
      const { error } = await supabase
        .from('feeding_records')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setRecords(prev => prev.filter(record => record.id !== id));
      return { error: null };
    } catch (error) {
      console.error('Error deleting feeding record:', error);
      return { error };
    }
  };

  return {
    records,
    loading,
    addRecord,
    updateRecord,
    deleteRecord,
    refetch: fetchRecords,
  };
};

// Hook para registros de sueño
export const useSleepRecords = (babyId: string | null) => {
  const [records, setRecords] = useState<SleepRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (babyId) {
      fetchRecords();
    } else {
      setRecords([]);
      setLoading(false);
    }
  }, [babyId]);

  const fetchRecords = async () => {
    try {
      const { data, error } = await supabase
      // Solo lo reciente.
      //
      // Antes se pedía TODO el historial del bebé cada vez que se abría
      // la pantalla. Con un bebé de un año son miles de filas: se
      // descargaban enteras, con datos móviles, para mostrar las de
      // arriba. Y encima el servidor corta en 1000 sin avisar, así que
      // el "historial completo" ya venía recortado en silencio.
      //
      // 500 cubre semanas de uso intenso. Para llevarse todo está
      // "Descargar mis datos", que sí trae hasta la última fila.
        .from('sleep_records')
        .select('*')
        .eq('baby_id', babyId)
        .order('start_time', { ascending: false })
        .limit(500);

      if (error) throw error;
      setRecords(data || []);
    } catch (error) {
      console.error('Error fetching sleep records:', error);
    } finally {
      setLoading(false);
    }
  };

  const addRecord = async (recordData: Omit<SleepRecord, 'id' | 'created_at'>) => {
    const { data, error, pendiente } = await guardarConRespaldo<SleepRecord>(
      'sleep_records',
      recordData as Record<string, unknown>,
    );
    if (data) setRecords(prev => [data, ...prev]);
    return { data, error, pendiente };
  };

  const updateRecord = async (id: string, updates: Partial<SleepRecord>) => {
    try {
      const { data, error } = await supabase
        .from('sleep_records')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setRecords(prev => prev.map(record => record.id === id ? data : record));
      return { data, error: null };
    } catch (error) {
      console.error('Error updating sleep record:', error);
      return { data: null, error };
    }
  };

  const deleteRecord = async (id: string) => {
    try {
      const { error } = await supabase
        .from('sleep_records')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setRecords(prev => prev.filter(record => record.id !== id));
      return { error: null };
    } catch (error) {
      console.error('Error deleting sleep record:', error);
      return { error };
    }
  };

  return {
    records,
    loading,
    addRecord,
    updateRecord,
    deleteRecord,
    refetch: fetchRecords,
  };
};

// Hook para registros de pañales
export const useDiaperRecords = (babyId: string | null) => {
  const [records, setRecords] = useState<DiaperRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (babyId) {
      fetchRecords();
    } else {
      setRecords([]);
      setLoading(false);
    }
  }, [babyId]);

  const fetchRecords = async () => {
    try {
      const { data, error } = await supabase
      // Solo lo reciente.
      //
      // Antes se pedía TODO el historial del bebé cada vez que se abría
      // la pantalla. Con un bebé de un año son miles de filas: se
      // descargaban enteras, con datos móviles, para mostrar las de
      // arriba. Y encima el servidor corta en 1000 sin avisar, así que
      // el "historial completo" ya venía recortado en silencio.
      //
      // 500 cubre semanas de uso intenso. Para llevarse todo está
      // "Descargar mis datos", que sí trae hasta la última fila.
        .from('diaper_records')
        .select('*')
        .eq('baby_id', babyId)
        .order('time', { ascending: false })
        .limit(500);

      if (error) throw error;
      setRecords(data || []);
    } catch (error) {
      console.error('Error fetching diaper records:', error);
    } finally {
      setLoading(false);
    }
  };

  const addRecord = async (recordData: Omit<DiaperRecord, 'id' | 'created_at'>) => {
    const { data, error, pendiente } = await guardarConRespaldo<DiaperRecord>(
      'diaper_records',
      recordData as Record<string, unknown>,
    );
    if (data) setRecords(prev => [data, ...prev]);
    return { data, error, pendiente };
  };

  const deleteRecord = async (id: string) => {
    try {
      const { error } = await supabase
        .from('diaper_records')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setRecords(prev => prev.filter(record => record.id !== id));
      return { error: null };
    } catch (error) {
      console.error('Error deleting diaper record:', error);
      return { error };
    }
  };

  return {
    records,
    loading,
    addRecord,
    deleteRecord,
    refetch: fetchRecords,
  };
};

// Hook para registros de crecimiento
export const useGrowthRecords = (babyId: string | null) => {
  const [records, setRecords] = useState<GrowthRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (babyId) {
      fetchRecords();
    } else {
      setRecords([]);
      setLoading(false);
    }
  }, [babyId]);

  const fetchRecords = async () => {
    try {
      const { data, error } = await supabase
        .from('growth_records')
        .select('*')
        .eq('baby_id', babyId)
        .order('recorded_at', { ascending: false });

      if (error) throw error;
      setRecords(data || []);
    } catch (error) {
      console.error('Error fetching growth records:', error);
    } finally {
      setLoading(false);
    }
  };

  const addRecord = async (recordData: Omit<GrowthRecord, 'id' | 'created_at'>) => {
    const { data, error, pendiente } = await guardarConRespaldo<GrowthRecord>(
      'growth_records',
      recordData as Record<string, unknown>,
    );
    if (data) setRecords(prev => [data, ...prev]);
    return { data, error, pendiente };
  };

  const deleteRecord = async (id: string) => {
    try {
      const { error } = await supabase
        .from('growth_records')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setRecords(prev => prev.filter(record => record.id !== id));
      return { error: null };
    } catch (error) {
      console.error('Error deleting growth record:', error);
      return { error };
    }
  };

  return {
    records,
    loading,
    addRecord,
    deleteRecord,
    refetch: fetchRecords,
  };
};

// Hook para hitos del desarrollo
export const useMilestones = (babyId: string | null) => {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (babyId) {
      fetchMilestones();
    } else {
      setMilestones([]);
      setLoading(false);
    }
  }, [babyId]);

  const fetchMilestones = async () => {
    try {
      const { data, error } = await supabase
        .from('milestones')
        .select('*')
        .eq('baby_id', babyId)
        .order('age_range_start', { ascending: true });

      if (error) throw error;
      setMilestones(data || []);
    } catch (error) {
      console.error('Error fetching milestones:', error);
    } finally {
      setLoading(false);
    }
  };

  const addMilestone = async (milestoneData: Omit<Milestone, 'id' | 'created_at'>) => {
    try {
      const { data, error } = await supabase
        .from('milestones')
        .insert([milestoneData])
        .select()
        .single();

      if (error) throw error;
      setMilestones(prev => [...prev, data]);
      return { data, error: null };
    } catch (error) {
      console.error('Error adding milestone:', error);
      return { data: null, error };
    }
  };

  const updateMilestone = async (id: string, updates: Partial<Milestone>) => {
    try {
      const { data, error } = await supabase
        .from('milestones')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setMilestones(prev => prev.map(milestone => milestone.id === id ? data : milestone));
      return { data, error: null };
    } catch (error) {
      console.error('Error updating milestone:', error);
      return { data: null, error };
    }
  };

  const deleteMilestone = async (id: string) => {
    try {
      const { error } = await supabase
        .from('milestones')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setMilestones(prev => prev.filter(milestone => milestone.id !== id));
      return { error: null };
    } catch (error) {
      console.error('Error deleting milestone:', error);
      return { error };
    }
  };

  return {
    milestones,
    loading,
    addMilestone,
    updateMilestone,
    deleteMilestone,
    refetch: fetchMilestones,
  };
};
